/**
 * Feature Highlights Configuration
 * 
 * Add new features here when releasing updates. The modal will show
 * all features from versions newer than the user's last_seen_version.
 * 
 * Versions should be in semver format (e.g., "1.2.0") and sorted newest first.
 * Icons can be any emoji or React icon component name.
 * 
 * Note: Only admins will see these highlights (controlled in FeatureHighlightModal).
 */

export const featureHighlights = {
  // Most recent version first
  "1.1.0": [
    {
      title: "Admin View Toggle",
      description: "Admins can now preview the app as an Estimator by clicking the '👁️ Admin View' button in the navbar. This helps you understand exactly what your team sees.",
      icon: "👁️"
    },
    {
      title: "Global Pricing Settings",
      description: "Configure default profit margins, overhead rates, and commission percentages in the new Pricing Settings tab. These values automatically apply to all new estimates.",
      icon: "⚙️"
    },
    {
      title: "Compact View Default",
      description: "The streamlined Compact View is now the default for all users. Admins can switch between Compact and Detailed views, while Estimators see only Compact and Results tabs for a focused workflow.",
      icon: "📐"
    },
    {
      title: "What's New Feature",
      description: "You're looking at it! Admins now see a summary of new features when logging into an updated version of the app.",
      icon: "✨"
    }
  ],
  // Add more versions as features are released:
  // "1.2.0": [
  //   {
  //     title: "Example Feature",
  //     description: "Description of what this feature does and how to use it.",
  //     icon: "🚀"
  //   },
  //   {
  //     title: "Another Feature",
  //     description: "Multiple features can be listed per version.",
  //     icon: "📊"
  //   }
  // ],
};

/**
 * Get all versions sorted from newest to oldest
 */
export const getSortedVersions = () => {
  return Object.keys(featureHighlights).sort((a, b) => {
    const [aMajor, aMinor, aPatch] = a.split('.').map(Number);
    const [bMajor, bMinor, bPatch] = b.split('.').map(Number);
    
    if (bMajor !== aMajor) return bMajor - aMajor;
    if (bMinor !== aMinor) return bMinor - aMinor;
    return bPatch - aPatch;
  });
};

/**
 * Compare two version strings
 * Returns: 1 if a > b, -1 if a < b, 0 if equal
 */
export const compareVersions = (a, b) => {
  if (!a) return -1; // null/undefined is less than any version
  if (!b) return 1;
  
  const [aMajor, aMinor, aPatch] = a.split('.').map(Number);
  const [bMajor, bMinor, bPatch] = b.split('.').map(Number);
  
  if (aMajor !== bMajor) return aMajor > bMajor ? 1 : -1;
  if (aMinor !== bMinor) return aMinor > bMinor ? 1 : -1;
  if (aPatch !== bPatch) return aPatch > bPatch ? 1 : -1;
  return 0;
};

/**
 * Get features for versions newer than lastSeenVersion
 * Returns up to maxVersions worth of features (default: 5)
 */
export const getNewFeatures = (lastSeenVersion, maxVersions = 5) => {
  const sortedVersions = getSortedVersions();
  const newFeatures = [];
  let versionsIncluded = 0;
  
  for (const version of sortedVersions) {
    // Stop if we've included enough versions
    if (versionsIncluded >= maxVersions) break;
    
    // Include this version if it's newer than lastSeenVersion (or if lastSeenVersion is null)
    if (compareVersions(version, lastSeenVersion) > 0) {
      newFeatures.push({
        version,
        features: featureHighlights[version]
      });
      versionsIncluded++;
    }
  }
  
  return newFeatures;
};

/**
 * Get the latest version number from the config
 */
export const getLatestConfigVersion = () => {
  const sortedVersions = getSortedVersions();
  return sortedVersions.length > 0 ? sortedVersions[0] : null;
};
